// Cumulative ("running total") transforms for the analyzer spend chart.
//
// The Daily chart answers "what changed, and when?"; the Cumulative chart answers
// "where are we heading relative to the period?". Both read the SAME fetched series
// — everything here is a pure running sum applied to the rows the daily pipeline
// already built, so cumulative mode costs no extra request.
//
// The one rule that makes the chart honest: the forecast CONTINUES the actual line,
// it does not restart. A cumulative forecast that began at zero on the handover day
// would draw a spend curve that collapses back to nothing halfway through the period.
//
// THE DISJOINT FLAG — read this before touching either accumulator. These functions
// were written for DAY rows, where `actual` and `forecast` on ONE row mean the same
// calendar day twice: the forecast RESTATES the day that already landed, so it is
// ANCHORED to the cumulative actual rather than added to it. That is true ONLY at day
// granularity. Once bucketing.js aggregates days into weeks or months, a bucket
// carrying both holds DISJOINT sums — the actual days summed, plus the forward days
// summed, with the handover day's forecast already dropped by bucketSingle. There the
// forecast must ADD ON TOP of the accumulated actual. Applying the anchor rule to a
// bucket throws the whole forward half of the handover bucket away, which is why the
// callers pass `disjoint` (true at week/month, false at day) and why every "handover"
// comment below says DAY or BUCKET rather than leaving it to the reader.
//
// The forecast line's end point equals the "Projected · period end" KPI card — at
// EVERY interval, which is the cross-check to run when touching any of this: Day,
// Week and Month must all land on the same projected period total, because they are
// three summaries of one set of days. They do so because
// both are scoped to the same selected period: this chart clips its rows to the
// period (see the daily pipeline), and the KPI comes from the backend's
// BuildPeriodProjection, which sums actuals-to-date plus forward forecast over that
// same period — and returns nothing rather than a partial figure when its forecast
// fan does not span the whole period. Neither side is a rolling horizon; that's why
// they agree.
//
// Interval note: the bands here are plain running sums of the daily bounds, which
// assumes daily forecast errors are perfectly correlated and so reads WIDER than a
// properly-derived cumulative interval. That is the conservative direction, and
// narrowing it is a forecasting decision (backend) rather than a charting one.

import { forecastKey } from "./forecastBreakdown";

// accumulateSingle turns the single-series rows ({ date, actual?, forecast?, lower?,
// upper? }) into running totals.
//
// `disjoint` is false for DAY rows and true for week/month BUCKETS (see the header).
//
// At day granularity a row carrying BOTH actual and forecast is the handover DAY: its
// forecast is ANCHORED to the cumulative actual rather than added to it, so the two
// lines meet exactly instead of the forecast jumping a day's spend above the actual.
//
// At week/month a bucket carrying both is the handover BUCKET, and its two fields are
// disjoint sums of different days — so the forecast is ADDED to the cumulative actual
// (cumForecast = cumActual + forecast). Anchoring there would silently discard every
// forward day inside that bucket, and the projected period total would fall short of
// the Day view's by exactly that amount.
//
// Rows past the boundary accumulate normally under both rules. The band opens from
// zero width where the forecast starts — there is no uncertainty about spend that
// already happened.
export function accumulateSingle(rows, disjoint = false) {
  let cumActual = 0;
  let cumForecast = null;
  let cumLower = 0;
  let cumUpper = 0;

  return (rows || []).map((row) => {
    const out = { ...row };

    if (row.actual != null) {
      cumActual += Number(row.actual) || 0;
      out.actual = cumActual;
    }

    if (row.forecast != null) {
      if (row.actual != null && !disjoint) {
        // Handover DAY: the forecast restates the day just counted, so re-anchor on
        // the actuals rather than double-counting it.
        cumForecast = cumActual;
        cumLower = cumForecast;
        cumUpper = cumForecast;
      } else {
        // Forward rows — and, when `disjoint`, the handover BUCKET, whose forecast is
        // the sum of days the actual does NOT cover. Seeding cumForecast from
        // cumActual here (which already includes this bucket's own actual, added
        // above) is what makes that "add on top" rather than "anchor to".
        if (cumForecast == null) {
          cumForecast = cumActual;
          cumLower = cumForecast;
          cumUpper = cumForecast;
        }
        cumForecast += Number(row.forecast) || 0;
        if (row.lower != null) cumLower += Number(row.lower) || 0;
        if (row.upper != null) cumUpper += Number(row.upper) || 0;
      }
      out.forecast = cumForecast;
      if (row.lower != null && row.upper != null) {
        out.lower = cumLower;
        out.upper = cumUpper;
        out.band = [cumLower, cumUpper];
      }
    }

    return out;
  });
}

// accumulateStacked turns the stacked breakdown rows into per-layer running totals.
//
// `stackKeys` are the actual layers; `forwardGroups` are the group names whose
// forward (forecast) layers ride on `<group>__forecast`. Each forward layer picks up
// from ITS OWN cumulative actual, so a segment keeps its share of the stack across
// the boundary. `disjoint` is false for DAY rows and true for week/month BUCKETS.
//
// At DAY granularity the actual keys are absent on forward rows (mergeForecastRows
// never puts both on one row), so a forward row only ever advances the forward
// totals, and each forward layer is emitted at its FULL cumulative value — which
// already contains the actual portion it grew out of. Emitting the actual keys
// alongside would stack that portion twice.
//
// At WEEK/MONTH the handover BUCKET legitimately carries both: actual days summed on
// `<group>`, forward days summed on `<group>__forecast`. Both must accumulate. The
// actual layers go into their running totals FIRST (skipping them, as the day rule
// did, froze `cum[k]` for the rest of the chart and left every later forward layer
// short by the whole handover bucket's actuals), the forward totals are then seeded
// from those updated running totals, and the forward layer is emitted as the
// INCREMENT above the actual layer drawn beneath it on that same row — Recharts
// stacks the two, so the bucket's height stays the projected total instead of
// counting the cumulative actual once per key family.
export function accumulateStacked(rows, stackKeys = [], forwardGroups = [], disjoint = false) {
  const cum = {};
  stackKeys.forEach((k) => { cum[k] = 0; });
  let cumTotal = 0;
  const cumForward = {};
  let forwardSeeded = false;
  let cumLower = 0;
  let cumUpper = 0;

  return (rows || []).map((row) => {
    const out = { ...row };
    const isForward = forwardGroups.some((g) => row[forecastKey(g)] != null);
    // Which actual layers this row actually DRAWS. A Set rather than an `out[k] != null`
    // test, because `out` starts as a copy of the row: only keys this pass rewrote into
    // running totals may be netted out of the forward layer below.
    const emitted = new Set();
    let rowActual = 0;

    if (!isForward || disjoint) {
      stackKeys.forEach((k) => {
        if (row[k] == null) return;
        const v = Number(row[k]) || 0;
        cum[k] += v;
        out[k] = cum[k];
        emitted.add(k);
        rowActual += v;
      });
    }

    if (isForward) {
      if (!forwardSeeded) {
        forwardSeeded = true;
        forwardGroups.forEach((g) => { cumForward[g] = cum[g] || 0; });
        // The band opens at zero width where the forecast starts. `cumTotal` covers
        // every PRIOR row's __total; on a handover bucket this row's own actual days
        // are not in it yet (its __total is added below and lumps actual with forward),
        // so `rowActual` supplies exactly the missing actual half. At day granularity
        // rowActual is 0 and this is the previous behaviour verbatim.
        cumLower = cumTotal + rowActual;
        cumUpper = cumTotal + rowActual;
      }
      forwardGroups.forEach((g) => {
        cumForward[g] += Number(row[forecastKey(g)]) || 0;
        out[forecastKey(g)] = emitted.has(g) ? cumForward[g] - (cum[g] || 0) : cumForward[g];
      });
      if (Array.isArray(row.__band)) {
        cumLower += Number(row.__band[0]) || 0;
        cumUpper += Number(row.__band[1]) || 0;
        out.__band = [cumLower, cumUpper];
      }
    }

    if (row.__total != null) {
      cumTotal += Number(row.__total) || 0;
      out.__total = cumTotal;
    }

    return out;
  });
}

// accumulateSeries is the running total of a plain value array — the comparison
// ghost, which is already aligned day-1-over-day-1 by offset. A null is a gap, not a
// reset: it stays null and the running total carries past it.
export function accumulateSeries(values) {
  let cum = 0;
  return (values || []).map((v) => {
    if (v == null) return null;
    cum += Number(v) || 0;
    return cum;
  });
}

// accumulateMeasures is the running total of { time, measure } points — the
// API-equivalent (shadow) overlay, which arrives in that shape.
export function accumulateMeasures(points) {
  let cum = 0;
  return (points || []).map((p) => {
    cum += Number(p.measure) || 0;
    return { ...p, measure: cum };
  });
}
